import { useEffect, useState } from 'react';
import { EventBus } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { apiHeaders, useMembersStore } from '../people/membersStore.js';
import { Button, Modal } from '../ds/index.js';

interface DuoBoard {
  kind: 'duo';
  itemId: string;
  claimId: string;
  roomName: string;
  mapName: string;
  partnerId: string | null;
  from: string;
  until: string;
  active: boolean;
}

interface DeskBoard {
  kind: 'desk';
  itemId: string;
  boardName: string;
  mapName: string;
  onDesk: boolean;
  updatedAt: string;
}

type MyBoard = DuoBoard | DeskBoard;

const day = (iso: string) => new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'long' }).format(new Date(iso));

/**
 * « Mes tableaux blancs » (menu de l'avatar) : les tableaux de son bureau
 * personnel (même retirés du bureau ou bureau libéré) et ceux dessinés dans
 * les salles revendiquées avec son binôme, gardés après la fin de la semaine.
 * « Ouvrir » les affiche en lecture seule, page par page, avec « Télécharger ».
 */
export function MyBoardsModal({ onClose }: { onClose: () => void }) {
  const [boards, setBoards] = useState<MyBoard[] | null>(null);
  const [failed, setFailed] = useState(false);
  const members = useMembersStore((s) => s.members);
  useEffect(() => {
    fetch(`${API_BASE}/world/whiteboards/mine`, { headers: apiHeaders() })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<MyBoard[]>; })
      .then(setBoards)
      .catch(() => setFailed(true));
  }, []);

  function open(b: MyBoard, name: string) {
    EventBus.emit('ui:board-open', b.kind === 'duo'
      ? { itemId: b.itemId, name, claimId: b.claimId }
      : { itemId: b.itemId, name, deskArchive: true });
    onClose();
  }

  return (
    <Modal title="Mes tableaux blancs" onClose={onClose}>
      <p className="pl-help" style={{ marginTop: 0 }}>
        Les tableaux de ton bureau et ceux des salles revendiquées avec ton binôme restent ici : ouvre-les pour les relire ou les télécharger page par page.
      </p>
      {failed && <p className="pl-help">Impossible de charger tes tableaux. Réessaie dans un instant.</p>}
      {!failed && boards === null && <p className="pl-help">Chargement…</p>}
      {boards?.length === 0 && <p className="pl-help">Aucun tableau pour l&apos;instant. Dessine sur le tableau de ton bureau ou sur celui d&apos;une salle Duo revendiquée.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {boards?.map((b) => {
          let title: string;
          let detail: string;
          let name: string;
          if (b.kind === 'desk') {
            title = `${b.boardName} · Mon bureau`;
            detail = `${b.mapName}, modifié le ${day(b.updatedAt)}${b.onDesk ? '' : ' (plus dans ton bureau)'}`;
            name = `${b.boardName} de mon bureau`;
          } else {
            const partner = b.partnerId ? members.get(b.partnerId)?.displayName ?? 'ton binôme' : 'ton binôme';
            title = `${b.roomName} · ${b.mapName}`;
            detail = `Avec ${partner}, du ${day(b.from)} au ${day(b.until)}${b.active ? ' (en cours)' : ''}`;
            name = `${b.roomName} - avec ${partner}`;
          }
          return (
            <div key={`${b.kind === 'duo' ? b.claimId : 'desk'}:${b.itemId}`} className="pl-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{title}</div>
                <div className="pl-help">{detail}</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => open(b, name)}>Ouvrir</Button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
